import { useRef, createContext, useContext, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { _TEST1234, api } from "../config/ApiConfig";
import { useTheme } from "../../hooks";

const MENU_ETC = [
    {
        menuNm: "My Task",
        menuId: "mytodo",
        path: "/ptlo/mypg/todo/ptlo0301001q?menuId=menu_ptli0005",
    },
    {
        menuNm: "Electronic Approval",
        menuId: "elecAprv",
        path: "",
    },
    {
        menuNm: "E-Document Form",
        menuId: "elecApp",
        path: "/ptle/elctrpt/elecdclr/ptle0601001s?menuId=menu_ptle0028",
    },
    {
        menuNm: "E-Document Temporary Saving",
        menuId: "elecTemp",
        path: "/ptle/elctrpt/elecdclr/ptle0605001s?menuId=menu_ptle0048",
    },
    {
        menuNm: "E-Document Processing Status",
        menuId: "elecPrcss",
        path: "/ptle/elctrpt/elecdclr/ptle0602001q?menuId=menu_ptle0029",
    },
];

const Context = createContext<any>(null);
const usePageContext = () => useContext(Context);

const pause = () => {
    return new Promise<void>((resolve) => {
        setTimeout(() => {
            resolve();
        }, 2000);
    });
};

const createAccessHistory = (pgeUid: any, menuId: any) => {
    const authorization = _TEST1234.AUTHORIZATION;
    const bsopClsfCd = process.env.REACT_APP_BASE?.slice(1, 4).toUpperCase();
    if (authorization && bsopClsfCd) {
        try {
            const portalApi = _TEST1234.PORTAL_API_ENV;
            const rqstUrlAddr = window.location.href;
            const pQuery = `?scrnId=${pgeUid}`;
            const mQuery = menuId ? `&menuId=${menuId}` : "";
            const rQuery = rqstUrlAddr ? `&rqstUrlAddr=${rqstUrlAddr}` : "";
            const bQuery = bsopClsfCd ? `&bsopClsfCd=${bsopClsfCd}` : "";
            const acssHistUrl = `${portalApi}/api/v1/ptl/comn/comn/acss-hist` + pQuery + mQuery + rQuery + bQuery;
            api.post(acssHistUrl);
        } catch (error) {
            console.log(error);
        }
    }
};

const getData = async (pgeUid: any, menuId: any, isFrom: any, lang: any) => {
    const portalApi = _TEST1234.PORTAL_API_ENV;
    let type = isFrom ? "scrn" : "dtl",
        name = isFrom ? "scrnDtl" : "menuDtl",
        ogaFlag = process.env.REACT_APP_SYSTEM_GROUP === "tanoga" ? "/oga" : "",
        base = `${portalApi}/api/v1/ptl/comn/comn/menu${ogaFlag}/`;
    const pQuery = `?scrnId=${pgeUid}`;
    const mQuery = `&menuId=${menuId}`;
    const url = base + type + pQuery + mQuery;
    const response = await axios.get(url, {
        headers: { "Accept-Language": lang },
    });
    return response.data[name];
};

const promise = (ref: any) => {
    const { menuId, pgeUid, isFrom, from, lang } = ref.current;
    return new Promise<void>(async (resolve, reject) => {
        const _fulfill = () => {
            ref.current.status = "fulfilled";
            resolve();
        };
        const _reject = () => {
            ref.current.status = "rejected";
            resolve();
        };

        if (!pgeUid || !menuId) {
            return _fulfill();
        }

        if (process.env.NODE_ENV === "production") {
            createAccessHistory(pgeUid, menuId);
        }

        try {
            const data: any = await getData(pgeUid, menuId, isFrom, lang.toUpperCase());
            if (isFrom) {
                data.pathList = [{ menuId, menuNm: from?.menuNm }];
            }

            ref.current.data = data;
            ref.current.pages[pgeUid] = {
                pgeUid,
                status: "fulfilled",
                promise: null,
                data,
            };

            return _fulfill();
        } catch (error) {
            return _reject();
        }
    });
};

const PageContextSuspenser = ({ children }: any) => {
    return <Suspense fallback={<div></div>}>{children}</Suspense>;
};

const PageContextFetcher = ({ children }: any) => {
    const { ref } = usePageContext();
    switch (ref.current.status) {
        case "fulfilled":
            return children;
        case "rejected":
            if (process.env.NODE_ENV === "production") {
                return children;
            } else {
                return children;
            }
        default:
            throw ref.current.promise;
    }
};

const PageContextProvider = ({ children }: any) => {
    if (process.env.NODE_ENV === "development") console.log("Page Context");

    const {
        theme: { lang },
    } = useTheme();
    const location = useLocation();
    const url = new URL(window.location.href);
    const history = useRef<any>([{ menuId: null, pgeUid: null }]);
    // Previous
    const prevPid = history.current[history.current.length - 1].pgeUid;
    const prevMid = history.current[history.current.length - 1].menuId;
    // Current page
    const pidRegex = /^[a-z]{4}[0-9]{7}[a-z]{1}$/;
    const currPid = location.pathname.split("/").find((arg) => pidRegex.test(arg));
    const isPage = Boolean(currPid);
    // Set current menu
    if (isPage && !url.searchParams.has("menuId") && prevMid) {
        url.searchParams.set("menuId", prevMid);
        window.history.replaceState(window.history.state, "", url);
    }
    // Current menu
    const currMid = url.searchParams.get("menuId");
    const isMenu = Boolean(currMid);

    const isPopup = url.searchParams.get("ppup") === "Y";
    const isFrom = MENU_ETC.some(({ menuId }) => menuId === currMid);
    location.state = { ...location.state, ...Object.fromEntries(url.searchParams.entries()) };

    const ref = useRef<any>({
        isPopup: null,
        isFrom: null,
        isPage: null,
        isMenu: null,
        pgeUid: null,
        menuId: null,
        from: null,
        lang: lang,

        data: {},
        pages: {},
        status: "pending",
        promise: null,

        menuEtc: MENU_ETC,
    });

    /** Reset context */
    if (prevPid !== currPid || prevMid !== currMid || ref.current.lang !== lang) {
        history.current.push({ menuId: currMid, pgeUid: currPid });
        ref.current.lang = lang;
        ref.current.isPopup = isPopup;
        ref.current.isFrom = isFrom;
        ref.current.isPage = isPage;
        ref.current.isMenu = isMenu;
        ref.current.pgeUid = currPid;
        ref.current.menuId = currMid;
        ref.current.from = isFrom ? MENU_ETC.find(({ menuId }) => menuId === currMid) : null;

        ref.current.data = {};
        ref.current.pages = {};
        ref.current.status = "pending";
        ref.current.promise = promise(ref);
    }

    return <Context.Provider value={{ history, ref }}>{children}</Context.Provider>;
};

const PageContext = {
    Provider: PageContextProvider,
    Fetcher: PageContextFetcher,
    Suspenser: PageContextSuspenser,
};

export { usePageContext };
export default PageContext;
